import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  Output,
  EventEmitter,
  Input,
  inject,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map, takeUntil, take, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';

import { CartProductListComponent } from '../../../shared/components/cart-product-list/cart-product-list.component';
import { CartProduct, CartPlan } from '../../../core/models/cart.model';
import { CurrencyBRLPipe } from '../../../shared/pipes/currency-brl.pipe';
import { CartFacade } from '../../../core/store/cart/cart.facade';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { CategoryIconMapperService } from '../../../shared/services/category-icon-mapper.service';
import { CartItem } from '../../../core/store/cart/cart.state';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import { CreateOrderRequest } from '../../../core/models/order.model';
import { PRESENCA_DIGITAL_UUIDS } from '../../../shared/constants/variation-uuids.constant';
import { buildCartDomainPromosForCartItem } from '../utils/cart-domain-promo-rules';
import {
  isOneTimeRegistryDomainPrice,
  isRegistryDomainCartPlanOrProduct,
} from '../../../core/utils/cart-domain-suggestion-rules';
import { getMindPromoUiFromProduct } from '../../../core/utils/mind-price.utils';

interface PersistedRequiredField {
  name: string;
}

const PRODUCT_DOMAIN_FIELD_NAMES = new Set(['google_ws_domain', 'microsoft_domain']);
const VARIATION_DOMAIN_FIELD_NAMES = new Set([
  'com_br_domain',
  'com_domain',
  'net_domain',
  'email_domain',
]);

@Component({
  selector: 'umkt-order-summary-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CartProductListComponent,
    CurrencyBRLPipe,
    IconComponent,
    I18nPipe,
  ],
  templateUrl: './order-summary-card.component.html',
  styleUrl: './order-summary-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderSummaryCardComponent implements OnInit, OnDestroy {
  private cartFacade = inject(CartFacade);
  private snack = inject(MatSnackBar);
  private cdr = inject(ChangeDetectorRef);
  private i18nService = inject(I18nService);
  private destroy$ = new Subject<void>();
  private categoryMapper = inject(CategoryIconMapperService);
  private validatedSvaPayload$ = new BehaviorSubject<CreateOrderRequest | null>(null);
  private latestProducts: CartProduct[] = [];
  private latestValidatedPayload: CreateOrderRequest | null = null;

  public totalValue = 0;
  public annualTotalValue = 0;
  public monthlyTotalValue = 0;
  public showAnnualTotal = false;
  public showMonthlyTotal = false;
  public showRegistrySplit = false;
  public firstMonthTotal = 0;
  public useValidatedOrderTotal = false;
  public discountCoupon = '';
  public discountValue = 0;
  public isCouponApplied = false;
  public showCouponInput = false;

  @Input() checkoutArea: boolean = true;
  @Input() maxQuantityByPlanId: Record<string, number> = {};

  @Input() set validatedSvaOrderPayload(value: CreateOrderRequest | null | undefined) {
    this.validatedSvaPayload$.next(value ?? null);
  }
  @Output() removeProduct = new EventEmitter<string>();
  @Output() removePlan = new EventEmitter<{
    productId: string;
    planId: string;
  }>();
  @Output() quantityDecreased = new EventEmitter<void>();
  @Output() openOrderDetails = new EventEmitter<void>();

  private readonly snackDuration = 6000;

  ngOnInit(): void {
    this.products$ = combineLatest([
      this.cartFacade.getCartItems(),
      this.validatedSvaPayload$,
    ]).pipe(
      map(([items, validated]) => ({
        products: this.mapItemsToProducts(items, validated),
        validated,
      })),
      tap(({ products, validated }) => {
        this.latestProducts = products;
        this.latestValidatedPayload = validated;
        this.recalculateTotal();
      }),
      map(({ products }) => products),
      takeUntil(this.destroy$)
    );

    this.products$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public products$: Observable<CartProduct[]> = of([]);

  private trimPersistedDomainFields(
    fields: PersistedRequiredField[] | undefined,
    allowedCount: number,
    domainFieldNames: ReadonlySet<string>
  ): PersistedRequiredField[] {
    const counters = new Map<string, number>();

    return (fields ?? []).filter(field => {
      if (!domainFieldNames.has(field.name)) {
        return true;
      }

      const nextCount = (counters.get(field.name) ?? 0) + 1;
      counters.set(field.name, nextCount);
      return nextCount <= allowedCount;
    });
  }

  private isPresencaDigitalQuantityDecrease(item: CartItem, nextQuantity: number): boolean {
    const baseVariationUuid = item.selectedVariation?.uuid ?? item.product?.uuid;
    const currentQuantity = item.quantity ?? 1;

    return !!baseVariationUuid &&
      PRESENCA_DIGITAL_UUIDS.includes(baseVariationUuid) &&
      nextQuantity < currentQuantity;
  }

  private hasStaleRequiredFields(
    item: CartItem,
    trimmedProductRequiredFields: PersistedRequiredField[],
    trimmedVariationRequiredFields: PersistedRequiredField[]
  ): boolean {
    const productFieldsChanged =
      trimmedProductRequiredFields.length !== (item.product.requiredFields?.length ?? 0);
    const variationFieldsChanged =
      trimmedVariationRequiredFields.length !== (item.selectedVariation?.requiredFields?.length ?? 0);

    return productFieldsChanged || variationFieldsChanged;
  }

  private clearStaleConfiguredDomains(item: CartItem, nextQuantity: number): void {
    if (!this.isPresencaDigitalQuantityDecrease(item, nextQuantity)) {
      return;
    }

    const trimmedProductRequiredFields = this.trimPersistedDomainFields(
      item.product.requiredFields,
      nextQuantity,
      PRODUCT_DOMAIN_FIELD_NAMES
    );
    const trimmedVariationRequiredFields = this.trimPersistedDomainFields(
      item.selectedVariation?.requiredFields,
      nextQuantity,
      VARIATION_DOMAIN_FIELD_NAMES
    );

    if (
      !this.hasStaleRequiredFields(item, trimmedProductRequiredFields, trimmedVariationRequiredFields)
    ) {
      return;
    }

    this.cartFacade.updateCartItem(item.id, {
      product: {
        ...item.product,
        requiredFields: trimmedProductRequiredFields,
      },
      selectedVariation: item.selectedVariation
        ? {
            ...item.selectedVariation,
            requiredFields: trimmedVariationRequiredFields,
          }
        : item.selectedVariation,
    });
  }

  private recalculateTotal(): void {
    const splitTotals = this.computeSplitTotals();
    this.annualTotalValue = splitTotals.annual;
    this.monthlyTotalValue = splitTotals.monthly;
    this.showAnnualTotal = false;
    this.showRegistrySplit = splitTotals.hasRegistryDomain;
    this.showMonthlyTotal = splitTotals.monthly > 0 && !splitTotals.hasRegistryDomain;
    this.firstMonthTotal = splitTotals.monthly + splitTotals.annual - this.discountValue;
    this.useValidatedOrderTotal = false;

    const validatedAmount = this.latestValidatedPayload?.payments?.[0]?.amount;
    if (validatedAmount != null && Number.isFinite(Number(validatedAmount))) {
      const validatedTotal = Number(validatedAmount) - this.discountValue;
      if (splitTotals.hasRegistryDomain) {
        this.firstMonthTotal = validatedTotal;
        this.totalValue = validatedTotal;
        return;
      }
      this.totalValue = validatedTotal;
      this.useValidatedOrderTotal = true;
      this.showAnnualTotal = false;
      this.showMonthlyTotal = false;
      this.showRegistrySplit = false;
      return;
    }

    if (this.showRegistrySplit) {
      this.totalValue = this.firstMonthTotal;
      return;
    }

    if (this.showMonthlyTotal) {
      this.totalValue = splitTotals.monthly - this.discountValue;
      return;
    }

    if (splitTotals.annual > 0) {
      this.totalValue = splitTotals.annual - this.discountValue;
      return;
    }

    this.totalValue =
      this.latestProducts.reduce((total, product) => total + (product.subtotal || 0), 0) -
      this.discountValue;
  }

  private computeSplitTotals(): {
    annual: number;
    monthly: number;
    hasRegistryDomain: boolean;
  } {
    let annual = 0;
    let monthly = 0;
    let hasRegistryDomain = false;

    for (const product of this.latestProducts) {
      for (const plan of product.plans) {
        const isRegistry = isRegistryDomainCartPlanOrProduct(plan.id, product.id);
        const qty = plan.quantity ?? 1;

        if (isRegistry) {
          hasRegistryDomain = true;
          annual += plan.unitPrice * qty;
          continue;
        }

        const adjustedPrice =
          plan.paymentRecurrence === 'annual' ? plan.unitPrice / 12 : plan.unitPrice;
        monthly += adjustedPrice * qty;

        // Domain-promos pagos (ex.: registro Google) entram no total anual.
        for (const promo of plan.domainPromos ?? []) {
          if (promo.isFree) continue;
          const promoPrice = Number(promo.price);
          if (!Number.isFinite(promoPrice) || promoPrice <= 0) continue;
          hasRegistryDomain = true;
          annual += promoPrice;
        }
      }
    }

    return { annual, monthly, hasRegistryDomain };
  }

  public calculateTotal(products: CartProduct[] = []): void {
    if (products.length > 0) {
      this.latestProducts = products;
    }
    this.recalculateTotal();
  }

  showCouponForm(): void {
    this.showCouponInput = true;
  }

  applyCoupon(): void {
    if (!this.discountCoupon.trim()) {
      this.snack.open(this.i18nService.instant('pages.orderSummaryCard.coupon.invalid'), 'OK', {
        duration: this.snackDuration,
        panelClass: ['snack-bar'],
      });
      return;
    }

    this.discountValue = 50.0;
    this.isCouponApplied = true;
    this.calculateTotal();

    this.snack.open(this.i18nService.instant('pages.orderSummaryCard.coupon.success'), 'OK', {
      duration: this.snackDuration,
      panelClass: ['snack-bar'],
    });

    this.cdr.detectChanges();
  }

  removeCoupon(): void {
    this.discountValue = 0;
    this.isCouponApplied = false;
    this.discountCoupon = '';
    this.showCouponInput = false;
    this.calculateTotal();
    this.cdr.detectChanges();
  }

  onRemoveProduct(productId: string): void {
    this.removeProduct.emit(productId);
    this.cartFacade.getCartItems().pipe(take(1)).subscribe(items => {
      const found = items.find(it => String(it.product?.uuid) === productId);
      if (found) {
        this.cartFacade.removeFromCart(found.id);
      }
    });
  }

  onRemovePlan(ev: { productId: string; planId: string }) {
    this.cartFacade.getCartItems().pipe(take(1)).subscribe(items => {
      const found = items.find(it => {
        const isMind = it.product?.productType === 'MIND';
        if (isMind) return String(it.product?.uuid) === ev.productId;
        return String(it.product?.uuid) === ev.productId && it.selectedVariation?.uuid === ev.planId;
      });
      if (found) {
        this.cartFacade.removeFromCart(found.id);
        this.removePlan.emit(ev);
      }
    });
  }

  onChangeQty(ev: { productId: string; planId: string; quantity: number }) {
    const maxQuantity = this.maxQuantityByPlanId[ev.planId];
    const quantity =
      maxQuantity != null && ev.quantity > maxQuantity ? maxQuantity : ev.quantity;

    this.cartFacade.getCartItems().pipe(take(1)).subscribe(items => {
      const found = items.find(it => {
        const isMind = it.product?.productType === 'MIND';
        if (isMind) return String(it.product?.uuid) === ev.productId;
        return String(it.product?.uuid) === ev.productId && it.selectedVariation?.uuid === ev.planId;
      });
      if (!found) return;

      if (quantity > 0) {
        this.clearStaleConfiguredDomains(found, quantity);
        const currentQuantity = found.quantity ?? 1;
        const wasDecremented = quantity < currentQuantity;
        const updated = this.cartFacade.updateCartItemQuantity(found.id, quantity);
        if (updated && wasDecremented) {
          queueMicrotask(() => this.quantityDecreased.emit());
        }
      } else {
        this.cartFacade.removeFromCart(found.id);
      }
    });
  }

  onOpenOrderDetails(): void {
    this.openOrderDetails.emit();
  }

  private mapItemsToProducts(
    items: CartItem[],
    validatedPayload: CreateOrderRequest | null
  ): CartProduct[] {
    interface Group { 
      name: string; 
      categories: string[]; 
      rows: CartItem[]; 
      termsUrl: string | null;
      productType?: string;
    }

    const byProduct = new Map<string, Group>();
    const validatedProducts = validatedPayload?.products ?? null;
    const validatedProductList = validatedProducts ?? [];

    for (const it of items ?? []) {
      if (!it.product?.uuid) continue;

      const key = it.product.uuid;
      const finalCategory = this.categoryMapper.mapProductToIconCategory(
        it.product.categories || [],
        it.product.productType
      );
      
      const categories = [finalCategory];
      const name = it.product.name ?? 'Produto';
      const termsUrl = it.product.termsUrl ?? null;

      let group = byProduct.get(key);
      if (!group) {
        group = { name, categories, rows: [], termsUrl, productType: it.product?.productType };
        byProduct.set(key, group);
      }
      group.rows.push(it);
    }

    const products: CartProduct[] = [];
    byProduct.forEach((group, key) => {
      const domainPromoOpts = {
        validatedProducts: validatedProductList,
        hideDomainPromosUntilValidated: true,
      };

      const plans: CartPlan[] = group.rows.map(row => {
        const isMind = row.product?.productType === 'MIND';
        const v = row.selectedVariation;
        const planId = isMind || !v ? row.product.uuid : v.uuid;
        const domainPromos = buildCartDomainPromosForCartItem(
          row,
          group.name,
          domainPromoOpts
        );

        if (isMind || !v) {
          const mindPromo = isMind
            ? getMindPromoUiFromProduct(row.product)
            : null;
          return {
            id: planId,
            name: row.product.name,
            unitPrice:
              mindPromo?.displayPrice ??
              row.product.salesPrice ??
              row.product.price ??
              0,
            originalUnitPrice: mindPromo?.hasPromotion
              ? mindPromo.originalPrice
              : undefined,
            hasPromotion: mindPromo?.hasPromotion ?? false,
            promotionMonths: mindPromo?.promotionMonths,
            paymentRecurrence:
              row.product.paymentRecurrence ?? 'monthly',
            quantity: row.quantity ?? 1,
            addons: [],
            domainPromos,
          };
        }

        return {
          id: planId,
          name: v.name ?? row.product.name,
          unitPrice:
            v.salesPrice ??
            v.price ??
            row.product.salesPrice ??
            row.product.price ??
            0,
          paymentRecurrence: v.paymentRecurrence,
          quantity: row.quantity ?? 1,
          addons: [],
          domainPromos,
        };
      });

      if (plans.length === 0) return;

      const subtotal = plans.reduce((acc, p) => {
        const adjustedPrice = isOneTimeRegistryDomainPrice(key, p.id)
          ? p.unitPrice
          : p.paymentRecurrence === 'annual'
            ? p.unitPrice / 12
            : p.unitPrice;
        return acc + adjustedPrice * (p.quantity ?? 1);
      }, 0);

      products.push({
        id: key,
        name: group.name,
        categories: group.categories,
        subtotal,
        plans,
        termsUrl: group.termsUrl,
        productType: group.productType,
      });
    });

    return products;
  }

}
